import {isFeatureEnabled} from 'feature-flags';
import {Flag, Parameters, Rank, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Integer, Right, Show, Text, VStack} from 'platform/foundation';
import {CurrencyFormatter, DateTimeFormat, NumberFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {ReactElement} from 'react';

import {isNil} from 'ramda';
import {floor, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {TRANSLATED_LANGUAGES} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {buildArray, CurrencyCodeType, EMPTY_PLACEHOLDER} from 'shared/web';

import {getFirstScrapedPrice} from '../../../components/PriceChangesPopper/getFirstScrapedPrice';
import {PriceChangesPopper} from '../../../components/PriceChangesPopper/PriceChangesPopper';
import {decimalToPercentRank} from '../../../utils/decimalToPercentRank';
import {detailSecondLine} from '../../../utils/detailSecondLine';
import {getDetailFirstLine, getDetailFirstLineForTooltip} from '../../../utils/getDetailFirstLine';
import {getLID} from '../../../utils/getLID';
import {getSuspiciousReasonsLabel} from '../../../utils/getSuspiciousReasonsLabel';
import {getVehicleDetailPosition} from '../../../utils/getVehicleDetailPosition';
import {isAuctionVehicle} from '../../../utils/isAuctionVehicle';
import {isClassifiedsAdInactive} from '../../../utils/isClassifiedsAdInactive';
import {AttractivenessFlag} from '../../AttractivenessFlag/AttractivenessFlag';
import {Clamp} from '../../Clamp/Clamp';
import {ClassifiedsSoldFlag} from '../../ClassifiedsSoldFlag/ClassifiedsSoldFlag';
import {HitlistBlacklistFlag} from '../../HitlistBlacklistFlag/HitlistBlacklistFlag';
import {SourcingCebiaReportBadge} from '../../SourcingCebiaReport/SourcingCebiaReportBadge';
import {HeaderCellTooltip} from '../components/HeaderCellTooltip';
import {CarsCell} from '../types/CarsCell';
import {getAdvertisedTime} from './getAdvertisedTime';

export type Column = {
  id: string;
  Header: ReactElement;
  Cell: (props: CarsCell) => ReactElement;
  width?: Integer;
};

export type GetSourcingTableColumnsParams = {
  locale: TRANSLATED_LANGUAGES;
  formatNumber: NumberFormatter;
  formatCurrency: CurrencyFormatter;
  isInComparison?: boolean;
  currency: string | undefined;
};

/*
Similarity page for auction fixed selected auction vehicle with classifieds vehicles
 */
export const getSourcingTableColumns = (params: GetSourcingTableColumnsParams) =>
  buildArray<Column>()
    .add({
      id: 'MAKE_MODEL_VARIANT_SPEC',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipMakeModelVariant')}>
          <Text size="xxSmall" color="secondary" noWrap>
            {i18n.t('entity.vehicle.labels.makeModelVariantSpec')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props: CarsCell) => {
        const formatNumber = useFormatNumber();
        const vehicleData = props.row.original.sourcingVehicle;
        const originalCar = props.row.original?.sourcingVehicle?.adId.includes('_DUPLICATE');
        const suspiciousReasons = getSuspiciousReasonsLabel(vehicleData?.suspiciousReason);
        const isInactive =
          !isAuctionVehicle(props.row.original) && isClassifiedsAdInactive(vehicleData);

        return (
          <VStack spacing={1}>
            <HStack spacing={1} align="center">
              <Show when={!!props.row.original.isNew}>
                <Tooltip label={i18n.t('entity.vehicle.labels.unseen')}>
                  <Box
                    backgroundColor="palettes.green.60.100"
                    width={3}
                    height={3}
                    borderRadius="circular"
                  />
                </Tooltip>
              </Show>
              <Show when={originalCar}>
                <Flag
                  label={i18n.t('entity.vehicle.labels.original')}
                  colorScheme="blue"
                  data-testid={testIds.sourcing.classifieds('originalCar-flag')}
                />
              </Show>
              <Show when={!isAuctionVehicle(props.row.original)}>
                <ClassifiedsSoldFlag sourcingVehicle={vehicleData} />
              </Show>
              <Tooltip label={getDetailFirstLineForTooltip(vehicleData)}>
                <Clamp lines={1}>
                  <Text size="small" alternative color={isInactive ? 'tertiary' : undefined}>
                    {getDetailFirstLine(vehicleData)}
                  </Text>
                </Clamp>
              </Tooltip>
              <Show when={!isAuctionVehicle(props.row.original)}>
                <SourcingCebiaReportBadge externalId={vehicleData.adId} />
              </Show>
              <Show when={isNotNilOrEmpty(props.row.original.vehicleSummary?.comment)}>
                <Tooltip label={props.row.original.vehicleSummary?.comment}>
                  <Icon value="communication/comment" size={5} />
                </Tooltip>
              </Show>
              <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                <HitlistBlacklistFlag sourcingVehicle={vehicleData} />
              </Show>
              <Show
                whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                when={vehicleData.suspicious}
              >
                <Tooltip label={suspiciousReasons}>
                  <Icon value="alert/warning" color="severity.warning" size={5} />
                </Tooltip>
              </Show>
            </HStack>
            <Parameters
              size="xSmall"
              color={isInactive ? 'tertiary' : 'secondary'}
              parameters={detailSecondLine(formatNumber, vehicleData)}
              data-testid={testIds.sourcing.classifieds('vehicleInfo-parameters')}
            />
          </VStack>
        );
      },
    })
    .add({
      id: 'POSITION',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipPosition')}>
          <Text size="xxSmall" color="secondary" align="center" noWrap>
            {i18n.t('entity.vehicle.labels.position')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const vehiclePosition = getVehicleDetailPosition(
          props.row.original.sourcingVehicle?.extraData?.marketPosition,
          props.row.original.sourcingVehicle?.extraData?.marketPositionCount,
          props.row.original.sourcingVehicle?.pricePercentile
        );
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);

        return (
          <VStack align="center">
            <Text size="xSmall" color={isInactive ? 'tertiary' : undefined} noWrap>
              {vehiclePosition[0]}
            </Text>
            <Text size="xxSmall" color="secondary">
              {vehiclePosition[1]}
            </Text>
          </VStack>
        );
      },
      width: 12,
    })
    .add({
      id: 'ORIGINAL_CZK',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipOriginalPrice')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.originalPrice')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const {originalWithVat, originalCurrency, currency} =
          props.row.original.sourcingVehicle?.price ?? {};
        const originalCurrencyCode = originalCurrency?.key as CurrencyCodeType;
        const convertedPrice = props.row.original.summary?.original;
        const convertedCurrencyCode = currency?.key as CurrencyCodeType;
        const firstScrapedPrice = getFirstScrapedPrice(
          props.row.original.sourcingVehicle,
          convertedPrice
        );
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);

        return (
          <VStack spacing={1} align="flex-end">
            <HStack spacing={1} align="center" justify="flex-end">
              <Show when={isNotNil(convertedPrice)}>
                <PriceChangesPopper
                  vehicle={props.row.original.sourcingVehicle}
                  currentPrice={convertedPrice ?? 0}
                  currency={convertedCurrencyCode}
                  popperPlacement="top-start"
                />
              </Show>
              <Tooltip
                label={
                  isNotNil(originalWithVat) &&
                  params.formatCurrency(originalWithVat, originalCurrencyCode)
                }
              >
                <Text
                  size="small"
                  alternative={!params.isInComparison}
                  color={isInactive ? 'tertiary' : undefined}
                >
                  {isNotNil(convertedPrice)
                    ? params.formatNumber(convertedPrice, 0)
                    : EMPTY_PLACEHOLDER}
                </Text>
              </Tooltip>
            </HStack>
            <Show
              when={
                isNotNil(convertedPrice) &&
                isNotNil(firstScrapedPrice) &&
                firstScrapedPrice !== convertedPrice
              }
              whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}
            >
              <Text size="xSmall" color="tertiary" strikethrough>
                {params.formatNumber(firstScrapedPrice, 0)}
              </Text>
            </Show>
          </VStack>
        );
      },
      width: 24,
    })
    .add({
      id: 'MARGIN',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.margin')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.margin')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const {vehicleCosts} = props.row.original?.summary ?? {};
        const maximalPossibleMargin =
          props.row.original?.sourcingVehicle?.extraData?.maximalPossibleMargin;
        const marginWithoutCost = params.formatNumber(
          (maximalPossibleMargin ?? 0) - (vehicleCosts ?? 0),
          0
        );
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);
        const marginColor = match({isInactive, isMarginNegative: (maximalPossibleMargin ?? 0) < 0})
          .with({isInactive: true}, () => 'tertiary' as const)
          .with({isMarginNegative: true}, () => 'danger' as const)
          .otherwise(() => undefined);

        const marginText = (
          <Text size="xSmall" align="right" color={marginColor}>
            {isNotNil(maximalPossibleMargin)
              ? params.formatNumber(maximalPossibleMargin, 0)
              : EMPTY_PLACEHOLDER}
          </Text>
        );

        if (isNil(maximalPossibleMargin)) {
          return marginText;
        }

        return (
          <Tooltip
            label={i18n.t('entity.vehicle.labels.marginWithoutTotalCosts', {
              marginWithoutCost,
            })}
          >
            {marginText}
          </Tooltip>
        );
      },
      width: 16,
    })
    .add({
      id: 'SELECTED_CZK',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRecommendedPrice')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.recommendedPrice')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const predictedPrice = props.row.original.sourcingVehicle?.extraData?.predictedPrice;
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);

        return (
          <VStack align="flex-end">
            <Text size="xSmall" color={isInactive ? 'tertiary' : undefined}>
              {isNotNil(predictedPrice)
                ? params.formatNumber(predictedPrice, 0)
                : EMPTY_PLACEHOLDER}
            </Text>
          </VStack>
        );
      },
      width: 16,
    })
    .add({
      id: 'LID',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipLID')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.LID')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const lid = getLID(props.row.original.summary?.lid);
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);
        return (
          <Text size="xSmall" align="right" color={isInactive ? 'tertiary' : undefined}>
            {isNotNil(lid) ? params.formatNumber(lid, 0) : EMPTY_PLACEHOLDER}
          </Text>
        );
      },
      width: 9,
    })
    .add({
      id: 'DOD',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipDOD')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.DOD')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => {
        const {firstOccurrenceDateTime, shouldShowTime, firstOccurrenceDistance} =
          getAdvertisedTime(props.row.original.sourcingVehicle, params.formatNumber);
        const isInactive =
          !isAuctionVehicle(props.row.original) &&
          isClassifiedsAdInactive(props.row.original.sourcingVehicle);
        const dodColor = match({isInactive, shouldShowTime})
          .with({isInactive: true}, () => 'tertiary' as const)
          .with({shouldShowTime: true}, () => 'success' as const)
          .otherwise(() => 'primary' as const);

        return (
          <Tooltip
            label={
              isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT) ? (
                <DateTimeFormat
                  value={firstOccurrenceDateTime}
                  format={shouldShowTime ? 'dateTimeShort' : 'dateShort'}
                />
              ) : undefined
            }
          >
            <Text size="xSmall" align="right" color={dodColor} noWrap overflowWrap="anywhere">
              {firstOccurrenceDistance}
            </Text>
          </Tooltip>
        );
      },
      width: 10,
    })
    .add({
      id: 'RANK',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRank')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.features')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => (
        <Tooltip label={decimalToPercentRank(props.row.original.summary?.rank ?? undefined)}>
          <HStack justify="flex-end">
            <Rank
              percentage={floor((props.row.original.summary?.rank ?? 0) * 100)}
              data-testid={testIds.sourcing.classifieds('rank-tooltip')}
            />
          </HStack>
        </Tooltip>
      ),
      width: 10,
    })
    .whenFeatureEnabled(featureFlags.SOURCING_ATTRACTIVENESS_SCORE, {
      id: 'ATTRACTIVENESS',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipAttractiveness')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.attractiveness')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: (props) => (
        <Right>
          <AttractivenessFlag sourcingVehicle={props.row.original.sourcingVehicle} />
        </Right>
      ),
      width: 10,
    });
